// PhonicsControls — one-click colour-coding for early-literacy charts.
// Mounted in TextCustomizePanel directly under the Emphasize section: both act
// on the letters INSIDE a text, so they belong together, but this one is
// automatic (the whole string, re-derived on every edit) where Emphasize is
// manual (the run the teacher picked).
//
// Teachers colour-code words to teach reading — vowels one colour and
// consonants another, alternating colours per syllable, or the blends and
// digraphs (sh, ch, th, ck) highlighted. By hand that is "select each letter,
// recolour", per letter, per word. This is one click, and it re-derives when
// the word is retyped.
//
// STATE: read straight off the object's data bag rather than through
// useSelectionProps — the sanctioned read-only peek other panels use. The
// recipe lives at data.phonics = {mode, colors} and is re-mirrored on every
// render, which is enough: every mutation here routes through apply(), and the
// panel re-renders on the object:modified that apply commits.
//
// The heuristics are GOOD-ENOUGH-FOR-CLASSROOM, not linguistically perfect —
// see the accuracy notes in lib/phonics.js. The hint below deliberately tells
// teachers to check the result, and Emphasize is right there to fix a word.
import React from 'react';
import { TbSegmented, TbSwatchRow } from '../../primitives/toolbarPrimitives';
import { PHONICS_DEFAULT_COLORS } from '../../lib/phonics';

const MODE_OPTIONS = [
  { value: 'none', label: 'Off' },
  { value: 'vowels', label: 'Vowels' },
  { value: 'syllables', label: 'Syllables' },
  { value: 'blends', label: 'Blends' },
];

// Print-safe, high-contrast picks that stay legible on a pale poster.
const LETTER_COLORS = ['#dc2626', '#2563eb', '#16a34a', '#ea580c', '#7c3aed', '#0f172a'];

const HINTS = {
  vowels: 'Vowels get the first colour, consonants the second. A “y” counts as a vowel when it sounds like one (my, happy).',
  syllables: 'Each word is split into syllables that alternate colours — but/ter. Check tricky words: the split is a good guess, not a dictionary.',
  blends: 'Highlights the letter teams that make one sound — sh, ch, th, ck, and blends like bl, st, tr.',
};

// hideHeader (2026-07-19 accordion sidebar): the sidebar hosts this inside an
// "ELA tools" accordion whose head already carries the title — skip the
// subhead there; the styles-modal rail keeps it. ("ELA tools" is the owner's
// name for the colour-coding toolset in the English version — phonics is the
// first ELA toolset; future languages get their own. Internal ids stay
// 'phonics'.)
export default function PhonicsControls({ apply, editor, selectedObjects, hideHeader = false }) {
  const obj = selectedObjects?.[0] || editor?.canvas?.getActiveObject();
  const recipe = obj?.data?.phonics || null;
  const mode = recipe?.mode ?? 'none';
  const colors = recipe?.colors || null;

  // The palette in play for the live mode, defaults filled in.
  const paletteFor = (m) => {
    if (m === 'vowels') return colors?.length >= 2 ? colors : PHONICS_DEFAULT_COLORS.vowels;
    if (m === 'syllables') return colors?.length >= 2 ? colors : PHONICS_DEFAULT_COLORS.syllables.slice(0, 2);
    if (m === 'blends') return colors?.length >= 1 ? colors : PHONICS_DEFAULT_COLORS.blends;
    return [];
  };
  const palette = paletteFor(mode);

  const setMode = (m) => apply(() => editor?.applyPhonics(m, m === mode ? colors : null), 'now');
  // Swap ONE slot of the live palette and re-apply the same mode.
  const setSlot = (i, c) => {
    const next = [...paletteFor(mode)];
    next[i] = c;
    apply(() => editor?.applyPhonics(mode, next), 'now');
  };

  // Slot labels, in the teacher's words — never "colour 1 / colour 2".
  const slotLabels = mode === 'vowels'
    ? ['Vowels', 'Consonants']
    : mode === 'syllables'
      ? ['1st, 3rd…', '2nd, 4th…']
      : mode === 'blends'
        ? ['Letter teams']
        : [];

  return (
    <div className="cce-inspector-section cce-phonics-section">
      {!hideHeader && <div className="cce-fx-subhead">ELA tools</div>}
      <TbSegmented
        label="ELA tools"
        value={mode}
        options={MODE_OPTIONS}
        onChange={setMode}
      />

      {mode === 'none' ? (
        <div className="cce-hint">
          Colour the letters automatically to teach reading — vowels, syllables, or letter teams.
        </div>
      ) : (
        <>
          {slotLabels.map((label, i) => (
            <div className="cce-phonics-slot" key={label}>
              <span className="cce-phonics-slot-label">{label}</span>
              <TbSwatchRow
                colors={LETTER_COLORS}
                value={palette[i] ?? null}
                onPick={(c) => setSlot(i, c)}
              />
            </div>
          ))}
          {mode === 'syllables' && (
            <button
              type="button"
              className={`cce-fx-chip${palette.length > 2 ? ' active' : ''}`}
              title="Rotate through a third colour before repeating"
              onClick={() => {
                const base = paletteFor('syllables');
                const next = palette.length > 2
                  ? base.slice(0, 2)
                  : [...base.slice(0, 2), PHONICS_DEFAULT_COLORS.syllables[2]];
                apply(() => editor?.applyPhonics('syllables', next), 'now');
              }}
            >
              Add a 3rd colour
            </button>
          )}
          <div className="cce-hint">{HINTS[mode]}</div>
        </>
      )}
    </div>
  );
}
